import { Component, ElementRef, inject, signal, ViewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { LoadingOverlayComponent } from '../../shared/components/loading-overlay/loading-overlay.component';
import { extractErrorMessage } from '../../shared/functions/extract-error-message.helper';
import { MemberService } from '../../shared/services/member.service';
import { NgbAlertModule } from '@ng-bootstrap/ng-bootstrap';

type DependentFormData = {
  first_name: string;
  last_name: string;
  relation: string;
  dob: string;
};

type MemberApplicationFormData = {
  first_name: string;
  last_name: string;
  dob: string;
  email_address: string;
  phone: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  zip_code: string;
  plan_type: string;
  dependents: DependentFormData[];
};

@Component({
  selector: 'app-patients-application-form',
  standalone: true,
  imports: [
    FormsModule,
    RouterLink,
    LoadingOverlayComponent,
    NgbAlertModule
  ],
  templateUrl: './patients-application-form.component.html',
  styleUrl: './patients-application-form.component.scss'
})
export class PatientsApplicationFormComponent {
  private service = inject(MemberService);

  @ViewChild('successMessage') successMessage?: ElementRef<HTMLElement>;

  loading = signal(false);
  displaySuccessMessage = signal(false);
  error = signal('');
  displayErrorMessage = signal(false);
  counter = 0;

  readonly duplicateSpouseMessage = 'Only one spouse can be added as a dependent.';
  readonly futureDobMessage = 'Date of Birth cannot be in the future.';
  readonly todayIso = this.getTodayIso();

  formData: MemberApplicationFormData = {
    first_name: '',
    last_name: '',
    dob: '',
    email_address: '',
    phone: '',
    address: '',
    address2: '',
    city: '',
    state: '',
    zip_code: '',
    plan_type: '',
    dependents: []
  };

  formatPhoneNumber(value: string): string {
    const digits = value.replace(/\D/g, '').slice(0, 10);

    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;

    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  onPhoneInput(value: string): void {
    this.formData.phone = value.replace(/\D/g, '').slice(0, 10);
  }

  addDependant(): void {
    if (this.formData.dependents.length >= 8) {
      return;
    }

    this.formData.dependents.push({
      first_name: '',
      last_name: '',
      relation: this.hasExistingSpouse() ? 'child' : '',
      dob: ''
    });
  }

  removeDependant(idx: number): void {
    this.formData.dependents.splice(idx, 1);
    this.clearDuplicateSpouseErrorIfResolved();
  }

  onPlanTypeChange(): void {
    this.clearError();

    if (this.formData.plan_type === 'Individual') {
      this.formData.dependents = [];
      return;
    }

    if (this.formData.plan_type === 'IndividualPlus') {
      this.formData.dependents = [
        {
          first_name: '',
          last_name: '',
          relation: 'spouse',
          dob: ''
        }
      ];
      return;
    }

    if (this.formData.plan_type === 'Family' && this.formData.dependents.length === 0) {
      this.addDependant();
    }
  }

  onDependentRelationChange(index: number): void {
    const dependent = this.formData.dependents[index];

    if (!dependent) {
      return;
    }

    const selectedRelation = dependent.relation?.toLowerCase();

    if (selectedRelation !== 'spouse') {
      this.clearDuplicateSpouseErrorIfResolved();
      return;
    }

    if (this.getSpouseCount() > 1) {
      dependent.relation = 'child';
      this.setError(this.duplicateSpouseMessage);
      return;
    }

    this.clearDuplicateSpouseErrorIfResolved();
  }

  shouldShowSpouseOption(index: number): boolean {
    const currentRelation = this.formData.dependents[index]?.relation?.toLowerCase();

    return currentRelation === 'spouse' || !this.hasExistingSpouse();
  }

  hasExistingSpouse(): boolean {
    return this.formData.dependents.some(
      dependent => dependent.relation?.toLowerCase() === 'spouse'
    );
  }

  hasMoreThanOneSpouse(): boolean {
    return this.getSpouseCount() > 1;
  }

  private getSpouseCount(): number {
    return this.formData.dependents.filter(
      dependent => dependent.relation?.toLowerCase() === 'spouse'
    ).length;
  }

  private clearDuplicateSpouseErrorIfResolved(): void {
    if (!this.hasMoreThanOneSpouse() && this.error() === this.duplicateSpouseMessage) {
      this.clearError();
    }
  }

  private setError(message: string): void {
    this.displayErrorMessage.set(true);
    this.error.set(message);
  }

  clearError(): void {
    this.displayErrorMessage.set(false);
    this.error.set('');
  }

  private validateDependents(): boolean {
    if (this.hasMoreThanOneSpouse()) {
      this.setError(this.duplicateSpouseMessage);
      return false;
    }

    return true;
  }

  isFutureDate(value: string): boolean {
    return !!value && value > this.todayIso;
  }

  hasFutureDob(): boolean {
    return this.isFutureDate(this.formData.dob) ||
      this.formData.dependents.some(dependent => this.isFutureDate(dependent.dob));
  }

  private getTodayIso(): string {
    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');

    return `${today.getFullYear()}-${month}-${day}`;
  }

  private validateDobDates(): boolean {
    if (this.isFutureDate(this.formData.dob)) {
      this.setError(this.futureDobMessage);
      return false;
    }

    if (this.hasFutureDob()) {
      this.setError(this.futureDobMessage);
      return false;
    }

    return true;
  }

  private scrollToSuccessMessage(): void {
    setTimeout(() => {
      const element = this.successMessage?.nativeElement;

      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        element.focus?.();
        return;
      }

      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, 100);
  }

  async submit(form?: NgForm): Promise<void> {
    this.clearError();

    if (form?.invalid || this.formData.phone.length !== 10) {
      form?.control.markAllAsTouched();
      return;
    }

    if (!this.validateDobDates()) {
      form?.control.markAllAsTouched();
      return;
    }

    if (!this.validateDependents()) {
      return;
    }

    try {
      this.loading.set(true);

      const apiResponse = await firstValueFrom(
        this.service.submitMemberForm(this.formData)
      );

      if (apiResponse) {
        this.displaySuccessMessage.set(true);
        this.scrollToSuccessMessage();
      }
    } catch (err: unknown) {
      this.setError(extractErrorMessage(err));
    } finally {
      this.loading.set(false);
    }
  }
}
